import React, {useCallback} from 'react'
import {
  Alerts,
  eventsSummaryDownload,
  getEventRecordIds,
  RPCDocs,
  useLocationContext,
  useRPCContext,
} from '@therms/react-modules'
import {
  Button,
  ContextMenu,
  Dropdown,
  Icon,
  toastManager,
} from '@therms/atalaya'
import {IoMdDownload} from 'react-icons/io'
import {MdDataset, MdRefresh} from 'react-icons/md'

type Event = RPCDocs.Events.GetEventsList.v1.Event
type Location = RPCDocs.Locations.GetLocationsList.v1.Location
type RecordListItem = RPCDocs.Records.GetRecordsList.v1.Record
type User = RPCDocs.Users.GetUsersList.v1.User

const getLocationAndUserIdsFromEvents = (events: Event[]) => {
  if (!Array.isArray(events) || !events.length) return {}

  const locationIdsById: Record<string, string> = {}
  const userIdsById: Record<string, string> = {}

  events.forEach((event) => {
    if (event.locationId && !locationIdsById[event.locationId])
      locationIdsById[event.locationId] = event.locationId
    if (event.assignedUsers.length) {
      event.assignedUsers.forEach((assignedUser) => {
        if (!userIdsById[assignedUser.userId])
          userIdsById[assignedUser.userId] = assignedUser.userId
      })
    }
  })

  return {
    locationIds: Object.keys(locationIdsById),
    userIds: Object.keys(userIdsById),
  }
}

const makeLocationsByIdFromLocations = (locations: Location[]) => {
  if (Array.isArray(locations) && locations.length) {
    return locations.reduce<Record<string, Location>>((acc, location) => {
      acc[location.id] = location
      return acc
    }, {})
  }

  return {}
}

const makeUsersByIdFromUsers = (users: User[]) => {
  if (Array.isArray(users) && users.length) {
    return users.reduce<Record<string, User>>((acc, user) => {
      acc[user.id] = user
      return acc
    }, {})
  }

  return {}
}

const makeRecordsByIdFromRecords = (records?: RecordListItem[]) => {
  if (Array.isArray(records) && records.length) {
    return records.reduce<Record<string, RecordListItem>>((acc, record) => {
      acc[record.id] = record
      return acc
    }, {})
  }

  return {}
}

interface BulkDownloadEventsDropdownProps {
  activityTypeIds?: string[]
  dateTimeRange: {start?: string; end?: string}
  eventTypeIds?: string[]
  locationId?: string
  searchText?: string
  userIdsAssigned?: string[]
}

function BulkDownloadEventsContextMenu({
  activityTypeIds,
  dateTimeRange,
  eventTypeIds,
  locationId,
  searchText,
  userIdsAssigned,
}: BulkDownloadEventsDropdownProps) {
  const locationCtx = useLocationContext()
  const {rpcService} = useRPCContext()

  const handleDownload = useCallback(
    async (config: {fileName?: string; format: 'csv' | 'excel'}) => {
      const toastId = Alerts.info(
        <div>
          <Icon Icon={MdRefresh} spin /> Downloading file, please wait...
        </div>,
        {autoClose: false, position: 'top-center'},
      )

      try {
        const eventsResponse = await rpcService.Events.GetEventsList.v1.call(
          {
            activityTypeIdFilter: activityTypeIds?.length
              ? activityTypeIds
              : undefined,
            assignedUserIdFilter: userIdsAssigned,
            eventTypeIds: eventTypeIds?.length ? eventTypeIds : undefined,
            fromDate: dateTimeRange?.start,
            locationId: locationCtx?.locationId ?? locationId,
            pagination: {limit: 5000},
            searchText,
            toDate: dateTimeRange?.end,
          },
          {timeout: 60_000},
        )

        const events = eventsResponse.data.events
        if (!events) return

        const {locationIds, userIds} = getLocationAndUserIdsFromEvents(events)

        const formIds = Array.from(
          new Set(
            events
              .map((event) => event.primaryDisposition?.formId)
              .filter((formId): formId is string => !!formId),
          ),
        )

        const [locationsResponse, usersResponse, formsResponse] =
          await Promise.all([
            rpcService.Locations.GetLocationsList.v1.call(
              {idFilter: locationIds, pagination: {limit: 5000}},
              {timeout: 60_000},
            ),
            rpcService.Users.GetUsersList.v1.call(
              {idFilter: userIds, pagination: {limit: 5000}},
              {timeout: 60_000},
            ),
            formIds.length
              ? rpcService.Forms.GetForms.v1.call({formIds}, {timeout: 60_000})
              : undefined,
          ])

        const locationsById = makeLocationsByIdFromLocations(
          locationsResponse.data.locations,
        )
        const usersById = makeUsersByIdFromUsers(usersResponse.data.users)
        const formsById = formsResponse?.data.formsById || {}

        const recordIds = getEventRecordIds(events, formsById)
        const recordsResponse = recordIds.length
          ? await rpcService.Records.GetRecordsList.v1.call(
              {idFilter: recordIds, pagination: {limit: 5000}},
              {timeout: 60_000},
            )
          : undefined
        const recordsById = makeRecordsByIdFromRecords(
          recordsResponse?.data.records,
        )

        eventsSummaryDownload(
          events,
          {formsById, locationsById, recordsById, usersById},
          {
            fileName:
              config.fileName ||
              `Events Summary ${new Date().toISOString()}`,
            format: config.format,
          },
        )
      } catch (err: any) {
        Alerts.error('There was a problem during download, please try again.')
        console.error('err downloading events', err)
      } finally {
        toastManager.dismiss(toastId)
      }
    },
    [
      activityTypeIds,
      dateTimeRange,
      eventTypeIds,
      locationCtx,
      locationId,
      rpcService,
      searchText,
      userIdsAssigned,
    ],
  )

  return (
    <Dropdown
      controller={({close, isOpen, open}) => (
        <Button onClick={isOpen ? close : open} subtle>
          <Icon Icon={IoMdDownload} />
        </Button>
      )}
    >
      <ContextMenu.Option onClick={() => handleDownload({format: 'csv'})}>
        <Icon Icon={MdDataset} /> <span>Download CSV</span>
      </ContextMenu.Option>

      <ContextMenu.Option onClick={() => handleDownload({format: 'excel'})}>
        <Icon Icon={MdDataset} /> <span>Download Excel</span>
      </ContextMenu.Option>
    </Dropdown>
  )
}

export {BulkDownloadEventsContextMenu}
